import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'

export const ConfirmMobile: React.FC = () => {
  const [status, setStatus] = useState<
    'loading' | 'success' | 'error' | 'no-app'
  >('loading')
  const [tokenHash, setTokenHash] = useState<string>('')
  const { tokenHash: routeTokenHash } = useParams<{ tokenHash: string }>()

  useEffect(() => {
    if (routeTokenHash) {
      setTokenHash(routeTokenHash)
      verifyAndRedirect(routeTokenHash)
    } else {
      setStatus('error')
    }
  }, [routeTokenHash])

  const verifyAndRedirect = async (token: string) => {
    try {
      // Verify the token with Supabase first
      const { error } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: 'signup'
      })

      if (error) {
        console.error('Token verification failed:', error)
        setStatus('error')
        return
      }

      // Token is valid, try to redirect to mobile app
      const deepLink = `luraapp://login/${token}`

      // Try to open the app
      window.location.href = deepLink

      // Set a timeout to show the "no app" message if the redirect doesn't work
      setTimeout(() => {
        setStatus('no-app')
      }, 2000)
    } catch (err) {
      console.error('Verification error:', err)
      setStatus('error')
    }
  }

  const handleRetryDeepLink = () => {
    if (tokenHash) {
      window.location.href = `luraapp://login/${tokenHash}`
    }
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
              <svg
                className="animate-spin h-6 w-6 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Confirming your email...
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Please wait while we verify your account and open the app
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Confirmation Failed
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              This confirmation link is invalid or has expired. Please try
              signing up again or contact support.
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (status === 'no-app') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Email Confirmed!
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Your email has been confirmed successfully.
            </p>
            <p className="mt-4 text-center text-sm text-gray-600">
              To complete the process, please download the Lura Health mobile
              app:
            </p>

            <div className="mt-6 space-y-4">
              <button
                onClick={handleRetryDeepLink}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Try Opening App Again
              </button>

              <div className="flex space-x-4">
                <a
                  href="https://apps.apple.com/app/placeholder" // Update with real iOS App Store link
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  📱 iOS App Store
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=placeholder" // Update with real Google Play Store link
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  🤖 Google Play
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return null
}
